import { Box, LinearProgress, Paper, Tooltip } from "@mui/material";
import Typography from "components/basic/typography";
import { AnalyticsBreakdownRowFragment } from "generated/graphql";
import { SERIES_COLORS } from "../../constants";
import { formatNumber } from "../../helpers/format";

interface BreakdownTableProps {
  title: string;
  rows: AnalyticsBreakdownRowFragment[];
  isLoading: boolean;
}

/**
 * A ranked top-ten list. A table rather than a pie or a donut: these are labels
 * with counts, and reading an exact value off an angle is the thing pies are
 * worst at. The inline bar is a magnitude cue, not the value itself.
 */
const BreakdownTable: React.FC<BreakdownTableProps> = ({
  title,
  rows,
  isLoading,
}) => {
  const max = rows[0]?.visitors ?? 0;

  return (
    <Paper variant="outlined" sx={{ padding: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {title}
      </Typography>

      <Box sx={{ marginTop: 1 }}>
        {isLoading && <LinearProgress />}

        {!isLoading && rows.length === 0 && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No data yet
          </Typography>
        )}

        {rows.map((row) => (
          <Box key={row.label} sx={{ marginBottom: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* Long paths and city names are truncated by noWrap, so the
                  full value has to stay reachable on hover. */}
              <Tooltip title={row.label} arrow>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {row.label}
                  </Typography>
                </Box>
              </Tooltip>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {formatNumber(row.visitors)}
              </Typography>
            </Box>

            <Box
              sx={{
                height: 4,
                marginTop: 0.5,
                borderRadius: "2px",
                width: max > 0 ? `${(row.visitors / max) * 100}%` : 0,
                backgroundColor: SERIES_COLORS.visitors,
              }}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default BreakdownTable;
